import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Grid } from "@mui/material";
import CustomText from "../components/CustomText";
import ImageLoader from "../components/ImageLoader";
import Footer from "../components/Footer";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { ScreenContext } from "../context/ScreenContext";
import { trackEvent } from "../utils/analytics";
import { openWhatsApp } from "../utils/openWhatsApp";
import { getInsuranceCompaniesFromApi } from "../services/insuranceService";
import InsuranceOption from "../components/InsuranceOption";
import { Helmet } from "react-helmet-async";

const img =
  "https://centromedicomedam.com/IMG/centromedicoMEDAM/imagen_prueba_promociones.png";
const InsuranceCompanies = () => {
  const location = useLocation();

  const { isMobile } = useContext(ScreenContext);
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [loadingInsurances, setLoadingInsurances] = useState(true);

  useEffect(() => {
    const loadInsurances = async () => {
      setLoadingInsurances(true);
      const data = await getInsuranceCompaniesFromApi();
      setInsuranceOptions(data);
      setLoadingInsurances(false);
    };

    loadInsurances();
  }, []);

  const handleInsuranceSelect = (_, value) => {
    if (!value) return;

    trackEvent("whatsapp_insurance", {
      page_path: location.pathname,
      page_url: window.location.href,
      section: "aseguradoras",
      insurance: value.name,
      value: 1,
    });

    openWhatsApp(
      "5951251494",
      `Hola Centro Médico MEDAM 👋.
Estoy interesado en atenderme con la aseguradora *${value.name}*.
¿Podrían ayudarme a validar mi cobertura y brindarme información sobre el proceso?
¡Gracias!`,
      true,
    );
  };
  return (
    <>
      <Helmet>
        <title>Aseguradoras y Convenios Médicos | Centro Médico Medam</title>
        <meta
          name="description"
          content="Conoce las aseguradoras con las que trabaja Centro Médico Medam. Consulta convenios médicos, valida tu cobertura y recibe atención profesional con respaldo de tu seguro."
        />
        <meta
          name="keywords"
          content="aseguradoras Centro Médico Medam, seguros médicos Texcoco, hospitales con aseguradora, GNP Medam, AXA Medam, MetLife Medam, atención médica con seguro, convenios médicos"
        />
        <meta name="author" content="Centro Médico Medam" />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://centromedicomedam.com/insuranceCompanies"
        />
        {/* Open Graph Facebook / WhatsApp */}
        <meta
          property="og:title"
          content="Aseguradoras y Convenios Médicos | Centro Médico Medam"
        />
        <meta
          property="og:description"
          content="Trabajamos con las principales aseguradoras para facilitar tu atención médica. Valida tu cobertura con Centro Médico Medam."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://centromedicomedam.com/insuranceCompanies"
        />
        <meta
          property="og:image"
          content="https://centromedicomedam.com/IMG/centromedicoMEDAM/imagen_prueba_promociones.png"
        />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Aseguradoras y Convenios Médicos | Centro Médico Medam"
        />
        <meta
          name="twitter:description"
          content="Encuentra información sobre aseguradoras, convenios y cómo utilizar tu seguro médico en Centro Médico Medam."
        />
        <meta
          name="twitter:image"
          content="https://centromedicomedam.com/IMG/centromedicoMEDAM/imagen_prueba_promociones.png"
        />
        {/* Datos estructurados Google */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalClinic",
            name: "Centro Médico Medam",
            url: "https://centromedicomedam.com",
            image:
              "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_Centro_Medico_Medam_Color.png",
            description:
              "Centro Médico Medam ofrece atención médica profesional y trabaja con diferentes aseguradoras para facilitar el acceso a servicios de salud.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Calpulalpan-Texcoco 105, Xala",
              addressLocality: "Texcoco de Mora",
              addressRegion: "Estado de México",
              postalCode: "56270",
              addressCountry: "MX",
            },
            medicalSpecialty: [
              "Consulta médica",
              "Servicios hospitalarios",
              "Atención con aseguradoras",
            ],
          })}
        </script>
      </Helmet>
      <CustomText
        variant="subtitle1"
        color="#333333"
        text="Aseguradoras"
        sx={design.Titulo}
      >
        Aseguradoras
      </CustomText>
      <Grid container width="100%">
        <Grid size={design.Grid1} />
        <Grid container size={design.Grid10} pt={3}>
          <Grid container size={design.Grid6} alignContent="center">
            <CustomText
              variant="subtitle1"
              color="#626262"
              text="En Medam, entendemos"
              sx={design.Description}
            >
              <strong>En MEDAM, tu salud siempre está primero.</strong>
              <br />
              Sabemos lo importante que es contar con el respaldo de tu
              aseguradora cuando lo necesitas. Por eso, trabajamos en convenio
              con las principales compañías de seguros del país, para que puedas
              acceder a servicios médicos de calidad sin complicaciones.
              <br />
              <br />
              <strong>¿Qué significa esto para ti?</strong>
              <br />
              Que tu experiencia será más simple, rápida y sin estrés. Desde tu
              ingreso hasta tu alta médica, te acompañamos en cada paso para que
              puedas enfocarte en lo más importante:{" "}
              <strong>recuperarte.</strong>
            </CustomText>
          </Grid>
          <Grid container size={design.Grid6}>
            <ImageLoader
              src={img}
              alt="aseguradoras"
              width={"93%"}
              height={isMobile ? 200 : 364}
            />
          </Grid>
        </Grid>
        <Grid size={design.Grid1} />
      </Grid>
      <CustomAutocomplete
        name="Buscar aseguradora"
        label="Buscar aseguradora"
        options={insuranceOptions}
        GridXS={12}
        Grid={12}
        required
        width="40%"
        getOptionLabel={(option) => option.name || option}
        renderOption={(props, option) => (
          <InsuranceOption props={props} option={option} />
        )}
        align
        onChange={handleInsuranceSelect}
        loading={loadingInsurances}
      />
      <Grid container width="100%" pt={2} pb={2}>
        <Grid size={design.Grid1} />
        <Grid container size={design.Grid10}>
          <Grid container size={design.Grid6} pt={3} alignContent="center">
            {ImgSeguros.map((doc, index) => (
              <Grid key={index} size={design.Grid6} sx={design.Align} pt={3}>
                <ImageLoader
                  src={doc.img}
                  alt="nuestros_valores_en_centro-medico_MEDAM"
                  width={"80%"}
                  height={71}
                />
              </Grid>
            ))}
          </Grid>
          <Grid size={design.Grid6} pt={3} alignContent="center">
            <CustomText
              variant="subtitle1"
              color="#626262"
              text="En Medam, entendemos"
              sx={design.Description}
            >
              En Centro Médico MEDAM trabajamos en convenio con una amplia gama
              de aseguradoras, brindándote acceso a servicios médicos de alta
              calidad con procesos simplificados. Si tu aseguradora no aparece
              en esta lista, <strong>contáctanos</strong>. Nuestro equipo estará
              encantado de ayudarte a validar tu cobertura y resolver cualquier
              duda.
            </CustomText>
          </Grid>
        </Grid>

        <Grid size={design.Grid1} />
      </Grid>
      <CustomText
        variant="subtitle1"
        color="#333333"
        text="Uso de seguros en Centro Médico Medam"
        sx={design.Titulo}
      >
        ¿Cómo usar tu seguro en MEDAM?
      </CustomText>
      <Grid container width="100%">
        <Grid size={design.Grid1} />
        <Grid container size={design.Grid10} pt={3}>
          <Grid container size={design.Grid6} alignContent="center">
            <CustomText
              variant="subtitle1"
              color="#626262"
              text="En Medam, entendemos"
              sx={design.Description}
            >
              <strong>1. Confirma tu convenio</strong>
              Verifica si tu aseguradora tiene acuerdo con nosotros.
              <br />
              <br />
              <strong>2. Presenta tus documentos</strong>
              Identificación oficial, póliza de seguro y cualquier autorización
              que se requiera.
              <br />
              <br />
              <strong>3. Validación rápida</strong>
              Nuestro equipo revisará y validará tu información para garantizar
              la correcta aplicación de tu cobertura.
            </CustomText>
          </Grid>
          <Grid container size={design.Grid6}>
            <ImageLoader
              src={img}
              alt="aseguradoras"
              width={"93%"}
              height={isMobile ? 200 : 364}
            />
          </Grid>
        </Grid>
        <Grid size={design.Grid1} />
      </Grid>
      <CustomText
        variant="subtitle1"
        color="#333333"
        text="Uso de seguros en Centro Médico Medam"
        sx={design.Titulo}
      >
        Al elegir MEDAM y tu aseguradora, obtienes:
      </CustomText>
      <Grid container width="100%">
        <Grid size={design.Grid1} />
        <Grid container size={design.Grid10} pt={3}>
          <Grid container size={design.Grid6}>
            <ImageLoader
              src={img}
              alt="aseguradoras"
              width={"93%"}
              height={isMobile ? 200 : 364}
            />
          </Grid>
          <Grid container size={design.Grid6} alignContent="center">
            <CustomText
              variant="subtitle1"
              color="#626262"
              text="En Medam, entendemos"
              sx={design.Description}
            >
              {Perso.map((text, index) => (
                <Box key={index} sx={design.Box}>
                  <Box component="span" sx={design.span}>
                    &bull;
                  </Box>
                  {text}
                </Box>
              ))}
            </CustomText>
          </Grid>
        </Grid>
        <Grid size={design.Grid1} />
      </Grid>
      <Footer />
    </>
  );
};

export default InsuranceCompanies;
const design = {
  Titulo: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: {
      xs: 25,
      sm: 25,
      md: 40,
      lg: 40,
    },
    fontWeight: 700,
    lineHeight: "normal",
    textAlign: "center",
    paddingTop: 2,
  },
  Description: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: {
      xs: 18,
      sm: 18,
      md: 20,
      lg: 20,
    },
    lineHeight: "normal",
    textAlign: "left",
  },
  Box: {
    display: "flex",
    alignItems: "flex-start",
    mb: 2,
    paddingLeft: 5,
    paddingRight: 5,
  },
  span: { pr: 1, lineHeight: "1.5" },
  Grid1: { xs: 1, md: 1, xl: 1, lg: 1 },
  Grid6: { xs: 12, md: 6, xl: 6, lg: 6 },
  Grid10: { xs: 10, md: 10, xl: 10, lg: 10 },
};

const ImgSeguros = [
  {
    img: "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_GNP_seguros.png",
  },
  {
    img: "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_Seguros_sura.png",
  },
  {
    img: "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_Assa compania_de seguros.png",
  },
  {
    img: "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_Chubb_seguros.png",
  },
  {
    img: "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_seguros_banorte.png",
  },
  {
    img: "https://centromedicomedam.com/IMG/centromedicoMEDAM/Logo_Seguros_MetLife.png",
  },
];

const Perso = [
  "Acceso a atención médica de calidad, con procesos administrativos claros y ágiles.",
  "Trato personalizado desde tu primer contacto.",
  "Tranquilidad, sabiendo que estás respaldado por profesionales y por tu seguro.",
];
